
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from '@/components/ui/use-toast';
import { AlertCircle, Shield } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const passwordSchema = z.object({
  currentPassword: z.string().min(1, { message: 'Current password is required' }),
  newPassword: z.string().min(8, { message: 'Password must be at least 8 characters' }),
  confirmPassword: z.string().min(8, { message: 'Password must be at least 8 characters' }),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PasswordFormValues = z.infer<typeof passwordSchema>;

const SecuritySettings: React.FC = () => {
  const { t } = useTranslation(['account']);
  const { toast } = useToast();
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  
  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onPasswordChange = async (data: PasswordFormValues) => {
    // In a real app, this would update the password in the database
    console.log('Password changed:', data);
    
    toast({
      title: t('account:passwordUpdated'),
      description: t('account:passwordUpdatedDesc'),
    });
    
    passwordForm.reset();
  };

  const handleTwoFactorToggle = (checked: boolean) => {
    setTwoFactorEnabled(checked);
    
    toast({
      title: checked ? t('account:twoFactorEnabled') : t('account:twoFactorDisabled'),
      description: checked ? t('account:twoFactorEnabledDesc') : t('account:twoFactorDisabledDesc'),
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('account:changePassword')}</CardTitle>
          <CardDescription>{t('account:passwordDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...passwordForm}>
            <form onSubmit={passwordForm.handleSubmit(onPasswordChange)} className="space-y-4">
              <FormField
                control={passwordForm.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('account:currentPassword')}</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={passwordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('account:newPassword')}</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('account:confirmPassword')}</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Button type="submit">{t('account:updatePassword')}</Button>
            </form>
          </Form>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>{t('account:twoFactorAuth')}</CardTitle>
          <CardDescription>{t('account:twoFactorDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <h4 className="font-medium">{t('account:enableTwoFactor')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('account:twoFactorExplanation')}
              </p>
            </div>
            <Switch
              checked={twoFactorEnabled}
              onCheckedChange={handleTwoFactorToggle}
            />
          </div>
          
          {twoFactorEnabled && (
            <Alert className="mt-4">
              <Shield className="h-4 w-4" />
              <AlertTitle>{t('account:twoFactorStatus')}</AlertTitle>
              <AlertDescription>
                {t('account:twoFactorActiveDesc')}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>{t('account:sessions')}</CardTitle>
          <CardDescription>{t('account:sessionsDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-1">
              <h4 className="font-medium">{t('account:chromeSession')}</h4>
              <p className="text-sm text-muted-foreground">{t('account:currentSession')}</p>
            </div>
            
            <div className="space-y-1">
              <h4 className="font-medium">{t('account:safariSession')}</h4>
              <p className="text-sm text-muted-foreground">{t('account:lastActiveSession')}</p>
            </div>
            
            <Button variant="outline" className="mt-4">
              {t('account:signOutAllSessions')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettings;
